import { useState } from "react";
import { Link } from "react-router-dom";
import { useGetFleetErrors } from "../../../api/routes/fleet";
import { MetricCard } from "./components/metric-card";
import { PeriodSelector } from "./components/period-selector";
import { Badge } from "../../../components/ui/badge";
import { AlertTriangle, Monitor, Hash, ChevronRight } from "lucide-react";
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    ResponsiveContainer,
} from "recharts";

const periodOptions = [
    { value: "24h", label: "Last 24 hours" },
    { value: "7d", label: "Last 7 days" },
    { value: "30d", label: "Last 30 days" },
];

const ErrorsSkeleton = () => (
    <div className="animate-pulse space-y-6">
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
            {[...Array(3)].map((_, i) => (
                <div key={i} className="h-24 bg-muted rounded-lg" />
            ))}
        </div>
        <div className="h-64 bg-muted rounded-lg" />
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
            <div className="h-64 bg-muted rounded-lg" />
            <div className="h-64 bg-muted rounded-lg" />
        </div>
    </div>
);

export const ErrorsTab = () => {
    const [period, setPeriod] = useState("7d");
    const { data, isLoading } = useGetFleetErrors(period);

    if (isLoading) return <ErrorsSkeleton />;
    if (!data) return null;

    const { summary, topErrors, errorsByDevice, dailyTrend } = data;

    return (
        <div className="space-y-6">
            {/* Period Selector */}
            <div className="flex justify-end">
                <PeriodSelector value={period} onChange={setPeriod} options={periodOptions} />
            </div>

            {/* Summary */}
            <div>
                <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
                    Error Summary
                </h2>
                <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                    <MetricCard
                        title="Total Errors"
                        value={summary.totalErrors}
                        icon={AlertTriangle}
                        color="bg-red-50 text-red-600 dark:bg-red-950 dark:text-red-400"
                    />
                    <MetricCard
                        title="Devices With Errors"
                        value={summary.devicesWithErrors}
                        icon={Monitor}
                        color="bg-orange-50 text-orange-600 dark:bg-orange-950 dark:text-orange-400"
                    />
                    <MetricCard
                        title="Unique Codes"
                        value={summary.uniqueErrorCodes}
                        icon={Hash}
                        color="bg-yellow-50 text-yellow-600 dark:bg-yellow-950 dark:text-yellow-400"
                    />
                </div>
            </div>

            {/* Daily Trend */}
            {dailyTrend.length > 0 && (
                <div>
                    <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
                        Error Trend
                    </h2>
                    <div className="rounded-lg border bg-card p-4 sm:p-5">
                        <ResponsiveContainer width="100%" height={250}>
                            <BarChart data={dailyTrend}>
                                <CartesianGrid strokeDasharray="3 3" className="stroke-border" />
                                <XAxis
                                    dataKey="date"
                                    tick={{ fontSize: 11 }}
                                    tickFormatter={(val) => {
                                        const d = new Date(val);
                                        return `${d.getMonth() + 1}/${d.getDate()}`;
                                    }}
                                    className="text-muted-foreground"
                                />
                                <YAxis tick={{ fontSize: 11 }} className="text-muted-foreground" allowDecimals={false} />
                                <Tooltip
                                    contentStyle={{
                                        backgroundColor: "hsl(var(--card))",
                                        border: "1px solid hsl(var(--border))",
                                        borderRadius: "8px",
                                        fontSize: "12px",
                                    }}
                                />
                                <Bar
                                    dataKey="errorCount"
                                    name="Errors"
                                    fill="hsl(0, 84%, 60%)"
                                    radius={[4, 4, 0, 0]}
                                />
                            </BarChart>
                        </ResponsiveContainer>
                    </div>
                </div>
            )}

            {/* Top Errors + Errors by Device */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
                {/* Top Errors */}
                <div>
                    <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
                        Top Errors
                    </h2>
                    <div className="rounded-lg border bg-card p-4 sm:p-5">
                        {topErrors.length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-6">No errors in this period</p>
                        ) : (
                            <div className="space-y-0">
                                {topErrors.map((error: any, i: number) => (
                                    <div
                                        key={error.errorNumber}
                                        className="flex items-center justify-between py-3 border-b border-border/50 last:border-0"
                                    >
                                        <div className="flex items-center gap-3 min-w-0 flex-1">
                                            <Badge variant="destructive" className="h-5 text-xs flex-shrink-0">
                                                {error.code || `#${error.errorNumber}`}
                                            </Badge>
                                            <span className="text-sm text-foreground truncate">
                                                {error.title}
                                            </span>
                                        </div>
                                        <span className="text-sm font-semibold text-foreground tabular-nums flex-shrink-0 ml-3">
                                            {error.occurrences}x
                                        </span>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                {/* Errors by Device */}
                <div>
                    <h2 className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-3">
                        Errors by Device
                    </h2>
                    <div className="rounded-lg border bg-card p-4 sm:p-5">
                        {errorsByDevice.length === 0 ? (
                            <p className="text-sm text-muted-foreground text-center py-6">No device errors in this period</p>
                        ) : (
                            <div className="space-y-0">
                                {errorsByDevice.map((device: any) => (
                                    <Link
                                        key={device.deviceId}
                                        to={`/devices/${device.deviceId}/errors`}
                                        className="block"
                                    >
                                        <div className="flex items-center justify-between py-3 border-b border-border/50 last:border-0 hover:bg-muted/30 -mx-4 px-4 transition-colors">
                                            <div className="min-w-0 flex-1">
                                                <p className="text-sm font-medium text-foreground truncate">
                                                    {device.deviceName}
                                                </p>
                                                <p className="text-xs text-muted-foreground truncate">
                                                    {device.businessName}
                                                    {device.topError && ` · Top: ${device.topError.code}`}
                                                </p>
                                            </div>
                                            <div className="flex items-center gap-2 flex-shrink-0 ml-3">
                                                <span className="text-sm font-semibold text-foreground tabular-nums">
                                                    {device.errorCount}
                                                </span>
                                                <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                            </div>
                                        </div>
                                    </Link>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
